import * as React from 'react'
import { UseFormGetValues } from 'react-hook-form'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { SearchFilter as Presenter, IProps as IPresenter } from './presenter'
import {
  searchEventIdContext,
  trackingUserContext,
} from '@/contexts/trackingContexts'
import { executeSearch, executeRegularSearch } from '@/utils/search'
import { trackEvent } from '@/utils/analyze'

interface IProps extends Omit<IPresenter, 'items'> {
  getValues: UseFormGetValues<ISearchConditionState>
}

type IDeteleSearchConditionHandler = (key: string) => void

interface ISearchFilterItem {
  label: string
  handler: IDeteleSearchConditionHandler
}

const execSearch = ({ search, regular_search, reserveType }: ISearchState) => {
  reserveType === 'normal'
    ? executeSearch({ search })
    : executeRegularSearch({ search, regular_search })
}

// 指導対象
const makeSearchFilterItemsGuidance = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []
  if (search.tutor_kindergarten) {
    items.push({
      label: t('search:params.search.tutorKindergarten'),
      handler: () =>
        handler(
          t('search:params.search.tutorKindergarten'),
          'tutor_kindergarten',
        ),
    })
  }
  if (search.tutor_elementary) {
    items.push({
      label: t('search:params.search.tutorElementary'),
      handler: () =>
        handler(t('search:params.search.tutorElementary'), 'tutor_elementary'),
    })
  }
  if (search.tutor_jrhighschool) {
    items.push({
      label: t('search:params.search.tutorJrhighschool'),
      handler: () =>
        handler(
          t('search:params.search.tutorJrhighschool'),
          'tutor_jrhighschool',
        ),
    })
  }
  if (search.tutor_highschool) {
    items.push({
      label: t('search:params.search.tutorHighschool'),
      handler: () =>
        handler(t('search:params.search.tutorHighschool'), 'tutor_highschool'),
    })
  }
  return items
}

// 保育可能年齢
const makeSearchFilterItemsNursery = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []
  if (search.nursery_age0_0) {
    items.push({
      label: t('search:params.search.nurseryAge0-0'),
      handler: () =>
        handler(t('search:params.search.nurseryAge0-0'), 'nursery_age0_0'),
    })
  }
  if (search.nursery_age0_1to2) {
    items.push({
      label: t('search:params.search.nurseryAge0-1to2'),
      handler: () =>
        handler(
          t('search:params.search.nurseryAge0-1to2'),
          'nursery_age0_1to2',
        ),
    })
  }
  if (search.nursery_age0_3to5) {
    items.push({
      label: t('search:params.search.nurseryAge0-3to5'),
      handler: () =>
        handler(
          t('search:params.search.nurseryAge0-3to5'),
          'nursery_age0_3to5',
        ),
    })
  }
  if (search.nursery_age0_6to11) {
    items.push({
      label: t('search:params.search.nurseryAge0-6to11'),
      handler: () =>
        handler(
          t('search:params.search.nurseryAge0-6to11'),
          'nursery_age0_6to11',
        ),
    })
  }
  if (search.nursery_age1) {
    items.push({
      label: t('search:params.search.nurseryAge1'),
      handler: () =>
        handler(t('search:params.search.nurseryAge1'), 'nursery_age1'),
    })
  }
  if (search.nursery_age2) {
    items.push({
      label: t('search:params.search.nurseryAge2'),
      handler: () =>
        handler(t('search:params.search.nurseryAge2'), 'nursery_age2'),
    })
  }
  if (search.nursery_age3to6) {
    items.push({
      label: t('search:params.search.nurseryAge3to6'),
      handler: () =>
        handler(t('search:params.search.nurseryAge3to6'), 'nursery_age3to6'),
    })
  }
  if (search.nursery_age7to12) {
    items.push({
      label: t('search:params.search.nurseryAge7to12'),
      handler: () =>
        handler(t('search:params.search.nurseryAge7to12'), 'nursery_age7to12'),
    })
  }
  return items
}

// 保育人数
const makeSearchFilterItemsAcceptable = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []
  if (search.acceptable_number1) {
    items.push({
      label: t('search:params.search.acceptableNumber1'),
      handler: () =>
        handler(
          t('search:params.search.acceptableNumber1'),
          'acceptable_number1',
        ),
    })
  }
  if (search.acceptable_number2) {
    items.push({
      label: t('search:params.search.acceptableNumber2'),
      handler: () =>
        handler(
          t('search:params.search.acceptableNumber2'),
          'acceptable_number2',
        ),
    })
  }
  return items
}

// 割引対象
const makeSearchFilterItemsDiscount = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.cao_bs) {
    items.push({
      label: t('search:params.search.caoBs'),
      handler: () => handler(t('search:params.search.caoBs'), 'cao_bs'),
    })
  }
  if (search.tokyo_bs_tmp_use) {
    items.push({
      label: t('search:params.search.tokyoBsTmpUse'),
      handler: () =>
        handler(t('search:params.search.tokyoBsTmpUse'), 'tokyo_bs_tmp_use'),
    })
  }
  if (search.tokyo_bs_waiting) {
    items.push({
      label: t('search:params.search.tokyoBsWaiting'),
      handler: () =>
        handler(t('search:params.search.tokyoBsWaiting'), 'tokyo_bs_waiting'),
    })
  }
  if (search.regular_discount) {
    items.push({
      label: t('search:params.search.regularDiscount'),
      handler: () =>
        handler(t('search:params.search.regularDiscount'), 'regular_discount'),
    })
  }
  if (search.campaign) {
    items.push({
      label: t('search:params.search.campaign'),
      handler: () => handler(t('search:params.search.campaign'), 'campaign'),
    })
  }
  if (search.housekeeping_subsidy) {
    items.push({
      label: t('search:params.search.housekeeping_subsidy'),
      handler: () =>
        handler(
          t('search:params.search.housekeeping_subsidy'),
          'housekeeping_subsidy',
        ),
    })
  }

  return items
}

// 時給
const makeSearchFilterItemsHourlyWage = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.hourly_wage_from_1000_to_1500) {
    items.push({
      label: t('search:params.search.hourlyWageFrom1000To1500'),
      handler: () =>
        handler(
          t('search:params.search.hourlyWageFrom1000To1500'),
          'hourly_wage_from_1000_to_1500',
        ),
    })
  }
  if (search.hourly_wage_from_1500_to_2000) {
    items.push({
      label: t('search:params.search.hourlyWageFrom1500To2000'),
      handler: () =>
        handler(
          t('search:params.search.hourlyWageFrom1500To2000'),
          'hourly_wage_from_1500_to_2000',
        ),
    })
  }
  if (search.hourly_wage_from_2000_to_2500) {
    items.push({
      label: t('search:params.search.hourlyWageFrom2000To2500'),
      handler: () =>
        handler(
          t('search:params.search.hourlyWageFrom2000To2500'),
          'hourly_wage_from_2000_to_2500',
        ),
    })
  }
  if (search.hourly_wage_from_2500_to_3000) {
    items.push({
      label: t('search:params.search.hourlyWageFrom2500To3000'),
      handler: () =>
        handler(
          t('search:params.search.hourlyWageFrom2500To3000'),
          'hourly_wage_from_2500_to_3000',
        ),
    })
  }
  if (search.hourly_wage_from_3000) {
    items.push({
      label: t('search:params.search.hourlyWageFrom3000'),
      handler: () =>
        handler(
          t('search:params.search.hourlyWageFrom3000'),
          'hourly_wage_from_3000',
        ),
    })
  }

  return items
}

// 資格
const makeSearchFilterItemsLicense = (
  search: INomalSearchCondition,
  deleteCredHandler: (label: string, key: string) => void,
  deleteSearchHandler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.cred?.childminder) {
    items.push({
      label: t('search:params.search.cred.childminder'),
      handler: () =>
        deleteCredHandler(
          t('search:params.search.cred.childminder'),
          'childminder',
        ),
    })
  }
  if (search.cred?.kindergarten_teacher) {
    items.push({
      label: t('search:params.search.cred.kindergartenTeacher'),
      handler: () =>
        deleteCredHandler(
          t('search:params.search.cred.kindergartenTeacher'),
          'kindergarten_teacher',
        ),
    })
  }
  if (search.cred?.nurse) {
    items.push({
      label: t('search:params.search.cred.nurse'),
      handler: () =>
        deleteCredHandler(t('search:params.search.cred.nurse'), 'nurse'),
    })
  }
  if (search.doula_certification) {
    items.push({
      label: t('search:params.search.doulaCertification'),
      handler: () =>
        deleteSearchHandler(
          t('search:params.search.doulaCertification'),
          'doula_certification',
        ),
    })
  }
  if (search.doula_certification_official) {
    items.push({
      label: t('search:params.search.doulaCertificationOfficial'),
      handler: () =>
        deleteSearchHandler(
          t('search:params.search.doulaCertificationOfficial'),
          'doula_certification_official',
        ),
    })
  }
  if (search.nursing_doula) {
    items.push({
      label: t('search:params.search.nursingDoula'),
      handler: () =>
        deleteSearchHandler(
          t('search:params.search.nursingDoula'),
          'nursing_doula',
        ),
    })
  }
  if (search.doulaedu_certification) {
    items.push({
      label: t('search:params.search.doulaeduCertification'),
      handler: () =>
        deleteSearchHandler(
          t('search:params.search.doulaeduCertification'),
          'doulaedu_certification',
        ),
    })
  }

  return items
}

// 家庭教師
const makeSearchFilterItemsTutor = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.tutor_tutor_supplementary) {
    items.push({
      label: t('search:params.search.tutorTutorSupplementary'),
      handler: () =>
        handler(
          t('search:params.search.tutorTutorSupplementary'),
          'tutor_tutor_supplementary',
        ),
    })
  }
  if (search.tutor_english_bilingual) {
    items.push({
      label: t('search:params.search.tutorEnglishBilingual'),
      handler: () =>
        handler(
          t('search:params.search.tutorEnglishBilingual'),
          'tutor_english_bilingual',
        ),
    })
  }
  if (search.tutor_entrance_exam) {
    items.push({
      label: t('search:params.search.tutorEntranceExam'),
      handler: () =>
        handler(
          t('search:params.search.tutorEntranceExam'),
          'tutor_entrance_exam',
        ),
    })
  }
  if (search.tutor_tokyo_university) {
    items.push({
      label: t('search:params.search.tutorTokyoUniversity'),
      handler: () =>
        handler(
          t('search:params.search.tutorTokyoUniversity'),
          'tutor_tokyo_university',
        ),
    })
  }
  if (search.tutor_keio_university) {
    items.push({
      label: t('search:params.search.tutorKeioUniversity'),
      handler: () =>
        handler(
          t('search:params.search.tutorKeioUniversity'),
          'tutor_keio_university',
        ),
    })
  }
  if (search.tutor_waseda_university) {
    items.push({
      label: t('search:params.search.tutorWasedaUniversity'),
      handler: () =>
        handler(
          t('search:params.search.tutorWasedaUniversity'),
          'tutor_waseda_university',
        ),
    })
  }
  if (search.tutor_sophia_university) {
    items.push({
      label: t('search:params.search.tutorSophiaUniversity'),
      handler: () =>
        handler(
          t('search:params.search.tutorSophiaUniversity'),
          'tutor_sophia_university',
        ),
    })
  }
  if (search.tutor_other_university) {
    items.push({
      label: t('search:params.search.tutorOtherUniversity'),
      handler: () =>
        handler(
          t('search:params.search.tutorOtherUniversity'),
          'tutor_other_university',
        ),
    })
  }

  return items
}

// 受験
const makeSearchFilterItemsExam = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.tutor_elementary_exam) {
    items.push({
      label: t('search:params.search.tutorElementaryExam'),
      handler: () =>
        handler(
          t('search:params.search.tutorElementaryExam'),
          'tutor_elementary_exam',
        ),
    })
  }
  if (search.tutor_jrhighschool_exam) {
    items.push({
      label: t('search:params.search.tutorJrhighschoolExam'),
      handler: () =>
        handler(
          t('search:params.search.tutorJrhighschoolExam'),
          'tutor_jrhighschool_exam',
        ),
    })
  }
  if (search.tutor_highschool_exam) {
    items.push({
      label: t('search:params.search.tutorHighschoolExam'),
      handler: () =>
        handler(
          t('search:params.search.tutorHighschoolExam'),
          'tutor_highschool_exam',
        ),
    })
  }
  if (search.tutor_college_exam) {
    items.push({
      label: t('search:params.search.tutorCollegeExam'),
      handler: () =>
        handler(
          t('search:params.search.tutorCollegeExam'),
          'tutor_college_exam',
        ),
    })
  }
  if (search.tutor_elementary_exam_ex) {
    items.push({
      label: t('search:params.search.tutorElementaryExamEx'),
      handler: () =>
        handler(
          t('search:params.search.tutorElementaryExamEx'),
          'tutor_elementary_exam_ex',
        ),
    })
  }
  if (search.tutor_jrhighschool_exam_ex) {
    items.push({
      label: t('search:params.search.tutorJrhighschoolExamEx'),
      handler: () =>
        handler(
          t('search:params.search.tutorJrhighschoolExamEx'),
          'tutor_jrhighschool_exam_ex',
        ),
    })
  }
  if (search.tutor_highschool_exam_ex) {
    items.push({
      label: t('search:params.search.tutorHighschoolExamEx'),
      handler: () =>
        handler(
          t('search:params.search.tutorHighschoolExamEx'),
          'tutor_highschool_exam_ex',
        ),
    })
  }
  if (search.tutor_college_exam_ex) {
    items.push({
      label: t('search:params.search.tutorCollegeExamEx'),
      handler: () =>
        handler(
          t('search:params.search.tutorCollegeExamEx'),
          'tutor_college_exam_ex',
        ),
    })
  }

  return items
}

// 科目
const makeSearchFilterItemsSubject = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.tutor_national_language) {
    items.push({
      label: t('search:params.search.tutorNationalLanguage'),
      handler: () =>
        handler(
          t('search:params.search.tutorNationalLanguage'),
          'tutor_national_language',
        ),
    })
  }
  if (search.tutor_arithmetic) {
    items.push({
      label: t('search:params.search.tutorArithmetic'),
      handler: () =>
        handler(t('search:params.search.tutorArithmetic'), 'tutor_arithmetic'),
    })
  }
  if (search.tutor_science) {
    items.push({
      label: t('search:params.search.tutorScience'),
      handler: () =>
        handler(t('search:params.search.tutorScience'), 'tutor_science'),
    })
  }
  if (search.tutor_social_studies) {
    items.push({
      label: t('search:params.search.tutorSocialStudies'),
      handler: () =>
        handler(
          t('search:params.search.tutorSocialStudies'),
          'tutor_social_studies',
        ),
    })
  }
  if (search.tutor_english) {
    items.push({
      label: t('search:params.search.tutorEnglish'),
      handler: () =>
        handler(t('search:params.search.tutorEnglish'), 'tutor_english'),
    })
  }
  if (search.tutor_english_speaking) {
    items.push({
      label: t('search:params.search.tutorEnglishSpeaking'),
      handler: () =>
        handler(
          t('search:params.search.tutorEnglishSpeaking'),
          'tutor_english_speaking',
        ),
    })
  }
  if (search.tutor_toeic) {
    items.push({
      label: t('search:params.search.tutorToeic'),
      handler: () =>
        handler(t('search:params.search.tutorToeic'), 'tutor_toeic'),
    })
  }
  if (search.tutor_toefl) {
    items.push({
      label: t('search:params.search.tutorToefl'),
      handler: () =>
        handler(t('search:params.search.tutorToefl'), 'tutor_toefl'),
    })
  }
  if (search.tutor_english_exam) {
    items.push({
      label: t('search:params.search.tutorEnglishExam'),
      handler: () =>
        handler(
          t('search:params.search.tutorEnglishExam'),
          'tutor_english_exam',
        ),
    })
  }
  if (search.tutor_math) {
    items.push({
      label: t('search:params.search.tutorMath'),
      handler: () => handler(t('search:params.search.tutorMath'), 'tutor_math'),
    })
  }
  if (search.tutor_ancient_writing) {
    items.push({
      label: t('search:params.search.tutorAncientWriting'),
      handler: () =>
        handler(
          t('search:params.search.tutorAncientWriting'),
          'tutor_ancient_writing',
        ),
    })
  }
  if (search.tutor_chinese_classics) {
    items.push({
      label: t('search:params.search.tutorChineseClassics'),
      handler: () =>
        handler(
          t('search:params.search.tutorChineseClassics'),
          'tutor_chinese_classics',
        ),
    })
  }
  if (search.tutor_essay) {
    items.push({
      label: t('search:params.search.tutorEssay'),
      handler: () =>
        handler(t('search:params.search.tutorEssay'), 'tutor_essay'),
    })
  }
  if (search.tutor_physics) {
    items.push({
      label: t('search:params.search.tutorPhysics'),
      handler: () =>
        handler(t('search:params.search.tutorPhysics'), 'tutor_physics'),
    })
  }
  if (search.tutor_chemistry) {
    items.push({
      label: t('search:params.search.tutorChemistry'),
      handler: () =>
        handler(t('search:params.search.tutorChemistry'), 'tutor_chemistry'),
    })
  }
  if (search.tutor_biology) {
    items.push({
      label: t('search:params.search.tutorBiology'),
      handler: () =>
        handler(t('search:params.search.tutorBiology'), 'tutor_biology'),
    })
  }
  if (search.tutor_geoscience) {
    items.push({
      label: t('search:params.search.tutorGeoscience'),
      handler: () =>
        handler(t('search:params.search.tutorGeoscience'), 'tutor_geoscience'),
    })
  }
  if (search.tutor_japanese_history) {
    items.push({
      label: t('search:params.search.tutorJapaneseHistory'),
      handler: () =>
        handler(
          t('search:params.search.tutorJapaneseHistory'),
          'tutor_japanese_history',
        ),
    })
  }
  if (search.tutor_world_history) {
    items.push({
      label: t('search:params.search.tutorWorldHistory'),
      handler: () =>
        handler(
          t('search:params.search.tutorWorldHistory'),
          'tutor_world_history',
        ),
    })
  }
  if (search.tutor_geography) {
    items.push({
      label: t('search:params.search.tutorGeography'),
      handler: () =>
        handler(t('search:params.search.tutorGeography'), 'tutor_geography'),
    })
  }
  if (search.tutor_ethics) {
    items.push({
      label: t('search:params.search.tutorEthics'),
      handler: () =>
        handler(t('search:params.search.tutorEthics'), 'tutor_ethics'),
    })
  }
  if (search.tutor_politics_economics) {
    items.push({
      label: t('search:params.search.tutorPoliticsEconomics'),
      handler: () =>
        handler(
          t('search:params.search.tutorPoliticsEconomics'),
          'tutor_politics_economics',
        ),
    })
  }

  return items
}

// サポート内容
const makeSearchFilterItemsSupport = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.pickup_only_support) {
    items.push({
      label: t('search:params.search.pickupOnlySupport'),
      handler: () =>
        handler(
          t('search:params.search.pickupOnlySupport'),
          'pickup_only_support',
        ),
    })
  }
  if (search.mid_night_support) {
    items.push({
      label: t('search:params.search.midNightSupport'),
      handler: () =>
        handler(t('search:params.search.midNightSupport'), 'mid_night_support'),
    })
  }
  if (search.handicap_support) {
    items.push({
      label: t('search:params.search.handicapSupport'),
      handler: () =>
        handler(t('search:params.search.handicapSupport'), 'handicap_support'),
    })
  }
  if (search.tutor_enable_flag) {
    items.push({
      label: t('search:params.search.tutorEnableFlag'),
      handler: () =>
        handler(t('search:params.search.tutorEnableFlag'), 'tutor_enable_flag'),
    })
  }
  if (search.english_lesson_support) {
    items.push({
      label: t('search:params.search.englishLessonSupport'),
      handler: () =>
        handler(
          t('search:params.search.englishLessonSupport'),
          'english_lesson_support',
        ),
    })
  }
  if (search.music_lesson_support) {
    items.push({
      label: t('search:params.search.musicLessonSupport'),
      handler: () =>
        handler(
          t('search:params.search.musicLessonSupport'),
          'music_lesson_support',
        ),
    })
  }
  if (search.sports_lesson_support) {
    items.push({
      label: t('search:params.search.sportsLessonSupport'),
      handler: () =>
        handler(
          t('search:params.search.sportsLessonSupport'),
          'sports_lesson_support',
        ),
    })
  }
  if (search.art_lesson_support) {
    items.push({
      label: t('search:params.search.artLessonSupport'),
      handler: () =>
        handler(
          t('search:params.search.artLessonSupport'),
          'art_lesson_support',
        ),
    })
  }
  if (search.other_appeal_support) {
    items.push({
      label: t('search:params.search.otherAppealSupport'),
      handler: () =>
        handler(
          t('search:params.search.otherAppealSupport'),
          'other_appeal_support',
        ),
    })
  }
  if (search.housekeeping_clean_up_support) {
    items.push({
      label: t('search:params.search.housekeepingCleanUpSupport'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingCleanUpSupport'),
          'housekeeping_clean_up_support',
        ),
    })
  }
  if (search.housekeeping_tidy_up_support) {
    items.push({
      label: t('search:params.search.housekeepingTidyUpSupport'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingTidyUpSupport'),
          'housekeeping_tidy_up_support',
        ),
    })
  }
  if (search.housekeeping_home_cooking_support) {
    items.push({
      label: t('search:params.search.housekeepingHomeCookingSupport'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingHomeCookingSupport'),
          'housekeeping_home_cooking_support',
        ),
    })
  }
  if (search.housekeeping_shopping) {
    items.push({
      label: t('search:params.search.housekeepingShopping'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingShopping'),
          'housekeeping_shopping',
        ),
    })
  }
  if (search.housekeeping_cooking_in_advance_support) {
    items.push({
      label: t('search:params.search.housekeepingCookingInAdvanceSupport'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingCookingInAdvanceSupport'),
          'housekeeping_cooking_in_advance_support',
        ),
    })
  }
  if (search.housekeeping_home_party_cooking) {
    items.push({
      label: t('search:params.search.housekeepingHomePartyCooking'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingHomePartyCooking'),
          'housekeeping_home_party_cooking',
        ),
    })
  }

  return items
}

// 対応時間帯
const makeSearchFilterItemsSupportTime = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.morning_support) {
    items.push({
      label: t('search:params.search.morningSupport'),
      handler: () =>
        handler(t('search:params.search.morningSupport'), 'morning_support'),
    })
  }
  if (search.night_support) {
    items.push({
      label: t('search:params.search.nightSupport'),
      handler: () =>
        handler(t('search:params.search.nightSupport'), 'night_support'),
    })
  }

  return items
}

// 病児
const makeSearchFilterItemsSick = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.sick_bsl_fever) {
    items.push({
      label: t('search:params.search.sickBslFever'),
      handler: () =>
        handler(t('search:params.search.sickBslFever'), 'sick_bsl_fever'),
    })
  }
  if (search.sick_bsl_unknown_disease) {
    items.push({
      label: t('search:params.search.sickBslUnknownDisease'),
      handler: () =>
        handler(
          t('search:params.search.sickBslUnknownDisease'),
          'sick_bsl_unknown_disease',
        ),
    })
  }
  if (search.sick_bsl_infection) {
    items.push({
      label: t('search:params.search.sickBslInfection'),
      handler: () =>
        handler(
          t('search:params.search.sickBslInfection'),
          'sick_bsl_infection',
        ),
    })
  }
  if (search.sick_bsl_unknown) {
    items.push({
      label: t('search:params.search.sickBslUnknown'),
      handler: () =>
        handler(t('search:params.search.sickBslUnknown'), 'sick_bsl_unknown'),
    })
  }

  return items
}

// 病後児
const makeSearchFilterItemsIllness = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.illness_bsl_fever) {
    items.push({
      label: t('search:params.search.illnessBslFever'),
      handler: () =>
        handler(t('search:params.search.illnessBslFever'), 'illness_bsl_fever'),
    })
  }
  if (search.illness_bsl_unknown_disease) {
    items.push({
      label: t('search:params.search.illnessBslUnknownDisease'),
      handler: () =>
        handler(
          t('search:params.search.illnessBslUnknownDisease'),
          'illness_bsl_unknown_disease',
        ),
    })
  }
  if (search.illness_bsl_infection) {
    items.push({
      label: t('search:params.search.illnessBslInfection'),
      handler: () =>
        handler(
          t('search:params.search.illnessBslInfection'),
          'illness_bsl_infection',
        ),
    })
  }

  return items
}

// 特徴
const makeSearchFilterItemsFeatures = (
  search: INomalSearchCondition,
  handler: (label: string, key: string) => void,
  t: TFunction,
): ISearchFilterItem[] => {
  const items: ISearchFilterItem[] = []

  if (search.no_option) {
    items.push({
      label: t('search:params.search.noOption'),
      handler: () => handler(t('search:params.search.noOption'), 'no_option'),
    })
  }
  if (search.foreign_language_support) {
    items.push({
      label: t('search:params.search.foreignLanguageSupport'),
      handler: () =>
        handler(
          t('search:params.search.foreignLanguageSupport'),
          'foreign_language_support',
        ),
    })
  }
  if (search.pickup_only_support) {
    items.push({
      label: t('search:params.search.pickupOnlySupport'),
      handler: () =>
        handler(
          t('search:params.search.pickupOnlySupport'),
          'pickup_only_support',
        ),
    })
  }
  if (search.handicap_support) {
    items.push({
      label: t('search:params.search.handicapSupport'),
      handler: () =>
        handler(t('search:params.search.handicapSupport'), 'handicap_support'),
    })
  }
  if (search.new_debuted_sitter) {
    items.push({
      label: t('search:params.search.newDebutedSitter'),
      handler: () =>
        handler(
          t('search:params.search.newDebutedSitter'),
          'new_debuted_sitter',
        ),
    })
  }
  if (search.housekeeping_clean_with_cooking) {
    items.push({
      label: t('search:params.search.housekeepingCleanWithCooking'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingCleanWithCooking'),
          'housekeeping_clean_with_cooking',
        ),
    })
  }
  if (search.housekeeping_cooking_for_food_industry) {
    items.push({
      label: t('search:params.search.housekeepingCookingForFoodIndustry'),
      handler: () =>
        handler(
          t('search:params.search.housekeepingCookingForFoodIndustry'),
          'housekeeping_cooking_for_food_industry',
        ),
    })
  }
  if (search.semi_childcare_certification) {
    items.push({
      label: t('search:params.search.semiChildcareCertification'),
      handler: () =>
        handler(
          t('search:params.search.semiChildcareCertification'),
          'semi_childcare_certification',
        ),
    })
  }

  return items
}

export const SearchFilter = ({ getValues }: IProps): JSX.Element => {
  const { t } = useTranslation()
  const user = React.useContext(trackingUserContext)
  const eventIds = React.useContext(searchEventIdContext)
  const reserveType = getValues('reserveType')
  const search = getValues('search')
  const regularSearch = getValues('regular_search')

  const deleteConditionAndSearch = (label: string, key: string) => {
    const items = [label]
    trackEvent({ ...eventIds.SEARCH_RESULT_FILTER_DELETE, items }, user)

    const newSearch = { ...search, [key]: undefined }
    execSearch({
      search: newSearch,
      regular_search: regularSearch,
      reserveType,
    })
  }
  const deleteGeocodeAndSearch = (geocode: IGeocode) => {
    const items = [geocode.name]
    trackEvent({ ...eventIds.SEARCH_RESULT_FILTER_DELETE, items }, user)

    const newGeocodes = {}
    Object.keys(search.geocode).forEach((prefectureId) => {
      newGeocodes[prefectureId] = (
        search.geocode[prefectureId] as IGeocode[]
      ).filter((g) => g.geocode_id !== geocode.geocode_id)
    })
    const newSearch = { ...search, geocode: newGeocodes }
    execSearch({
      search: newSearch,
      regular_search: regularSearch,
      reserveType,
    })
  }
  const deleteStationAndSearch = (station: IStation) => {
    const items = [station.name]
    trackEvent({ ...eventIds.SEARCH_RESULT_FILTER_DELETE, items }, user)

    const newStations = {}
    Object.keys(search.station).forEach((prefectureId) => {
      newStations[prefectureId] = (
        search.station[prefectureId] as IStation[]
      ).filter((s) => s.slug !== station.slug)
    })
    const newSearch = { ...search, station: newStations }
    execSearch({
      search: newSearch,
      regular_search: regularSearch,
      reserveType,
    })
  }
  const deleteCredAndSearch = (label: string, key: string) => {
    const items = [label]
    trackEvent({ ...eventIds.SEARCH_RESULT_FILTER_DELETE, items }, user)

    const newCred = { ...search.cred, [key]: undefined }
    const newSearch = { ...search, cred: newCred }
    execSearch({
      search: newSearch,
      regular_search: regularSearch,
      reserveType,
    })
  }

  // 一次元配列に変換
  const geocodes: IGeocode[] = ([] as IGeocode[]).concat(
    ...Object.values(search.geocode),
  )
  const stations: IStation[] = ([] as IStation[]).concat(
    ...Object.values(search.station),
  )

  const items = []
  // 予約可能
  if (search.available_schedule) {
    items.push({
      label: '予約可能',
      key: 'filter-item-available-schedule',
      handler: () => deleteConditionAndSearch('予約可能', 'available_schedule'),
    })
  }

  // エリア
  if (geocodes.length) {
    items.push(
      ...geocodes.map((geocode) => ({
        label: geocode.name,
        key: `filter-item-geocode-${geocode.geocode_id}`,
        handler: () => deleteGeocodeAndSearch(geocode),
      })),
    )
  }

  // 駅
  if (stations.length) {
    items.push(
      ...stations.map((station) => ({
        label: station.name,
        key: `filter-item-station-${station.slug}`,
        handler: () => deleteStationAndSearch(station),
      })),
    )
  }

  // 指導対象
  items.push(
    ...makeSearchFilterItemsGuidance(search, deleteConditionAndSearch, t),
  )

  // 保育可能年齢
  items.push(
    ...makeSearchFilterItemsNursery(search, deleteConditionAndSearch, t),
  )

  // 保育人数
  items.push(
    ...makeSearchFilterItemsAcceptable(search, deleteConditionAndSearch, t),
  )

  // 割引対象
  items.push(
    ...makeSearchFilterItemsDiscount(search, deleteConditionAndSearch, t),
  )

  // 時給
  items.push(
    ...makeSearchFilterItemsHourlyWage(search, deleteConditionAndSearch, t),
  )

  // 資格
  items.push(
    ...makeSearchFilterItemsLicense(
      search,
      deleteCredAndSearch,
      deleteConditionAndSearch,
      t,
    ),
  )

  // 家庭教師
  items.push(...makeSearchFilterItemsTutor(search, deleteConditionAndSearch, t))

  // 受験
  items.push(...makeSearchFilterItemsExam(search, deleteConditionAndSearch, t))

  // 科目
  items.push(
    ...makeSearchFilterItemsSubject(search, deleteConditionAndSearch, t),
  )

  // サポート内容
  items.push(
    ...makeSearchFilterItemsSupport(search, deleteConditionAndSearch, t),
  )

  // 対応時間帯
  items.push(
    ...makeSearchFilterItemsSupportTime(search, deleteConditionAndSearch, t),
  )

  // 病児
  items.push(...makeSearchFilterItemsSick(search, deleteConditionAndSearch, t))

  // 病後児
  items.push(
    ...makeSearchFilterItemsIllness(search, deleteConditionAndSearch, t),
  )

  // 特徴
  items.push(
    ...makeSearchFilterItemsFeatures(search, deleteConditionAndSearch, t),
  )

  // キーワード
  if (search.word) {
    const label = `検索: ${search.word}`
    items.push({
      label,
      handler: () => deleteConditionAndSearch(label, 'word'),
    })
  }

  if (items.length === 0) {
    return null
  }
  return <Presenter items={items} />
}

export default SearchFilter
